// Emote catalog: the sounds + emojis a player can fire from the radial wheel (press
// E). Sound emotes are synthesized in SoundManager (no audio assets); emoji emotes
// are purely visual. The wheel layout (which emote sits in each slot) is a per-user
// preference saved to localStorage and edited in the wheel's Configure view.

export type EmoteKind = 'emoji' | 'sound';
// A sound key is either a built-in synthesized emote (clap/nice/…) or a voice-line
// sample id registered via SoundManager.setEmoteSamples. Keys with no synth case in
// SoundManager.emote() simply play their sample (and are silent if the file 404s).
export type EmoteSound = string;

export interface EmoteDef {
  id: string;
  kind: EmoteKind;
  label: string;
  /** Emoji shown in the wheel and as the floating bubble when fired. */
  glyph: string;
  /** Synthesized sound to play (sound emotes only). */
  sound?: EmoteSound;
}

export const EMOTES: EmoteDef[] = [
  // --- sound emotes (also pop their glyph as a bubble) ---
  { id: 'clap', kind: 'sound', label: 'Clap', glyph: '👏', sound: 'clap' },
  { id: 'nice', kind: 'sound', label: 'Nice shot', glyph: '✨', sound: 'nice' },
  { id: 'cheer', kind: 'sound', label: 'Cheer', glyph: '🎉', sound: 'cheer' },
  { id: 'whistle', kind: 'sound', label: 'Whistle', glyph: '😮', sound: 'whistle' },
  { id: 'boo', kind: 'sound', label: 'Boo', glyph: '👎', sound: 'boo' },
  { id: 'drumroll', kind: 'sound', label: 'Drumroll', glyph: '🥁', sound: 'drumroll' },
  // --- Korean 당구 voice-line emotes (real recorded samples in public/sfx/voice) ---
  { id: 'v_kabi', kind: 'sound', label: '아 까비', glyph: '😩', sound: 'v_kabi' },
  { id: 'v_niceshot', kind: 'sound', label: '나이스 샷', glyph: '🎯', sound: 'v_niceshot' },
  { id: 'v_goodshot', kind: 'sound', label: '굿샷', glyph: '👍', sound: 'v_goodshot' },
  { id: 'v_taeyang', kind: 'sound', label: '태양아 잘 쳤어', glyph: '🌞', sound: 'v_taeyang' },
  { id: 'v_jjalne', kind: 'sound', label: '이런 짤네', glyph: '🙄', sound: 'v_jjalne' },
  { id: 'v_jjalne2', kind: 'sound', label: '이런 짤네 (2)', glyph: '🙄', sound: 'v_jjalne2' },
  { id: 'v_angaju', kind: 'sound', label: '안 가주?', glyph: '🤨', sound: 'v_angaju' },
  { id: 'v_jayu', kind: 'sound', label: '자유쥬?', glyph: '❓', sound: 'v_jayu' },
  { id: 'v_mwonya', kind: 'sound', label: '뭐냐 이게', glyph: '😵', sound: 'v_mwonya' },
  { id: 'v_gensei', kind: 'sound', label: '겐세이 하지마', glyph: '✋', sound: 'v_gensei' },
  { id: 'v_coffee', kind: 'sound', label: '냉커피? 콜라?', glyph: '☕', sound: 'v_coffee' },
  // --- emoji emotes (visual only) ---
  { id: 'thumbsup', kind: 'emoji', label: 'Nice', glyph: '👍' },
  { id: 'fire', kind: 'emoji', label: 'Fire', glyph: '🔥' },
  { id: 'laugh', kind: 'emoji', label: 'Laugh', glyph: '😂' },
  { id: 'cool', kind: 'emoji', label: 'Cool', glyph: '😎' },
  { id: 'cry', kind: 'emoji', label: 'Cry', glyph: '😢' },
  { id: 'mindblown', kind: 'emoji', label: 'Wow', glyph: '🤯' },
  { id: 'pray', kind: 'emoji', label: 'Please', glyph: '🙏' },
  { id: 'salt', kind: 'emoji', label: 'Salty', glyph: '🧂' },
  { id: 'eightball', kind: 'emoji', label: '8-ball', glyph: '🎱' },
  { id: 'gg', kind: 'emoji', label: 'GG', glyph: '🤝' },
];

export const EMOTES_BY_ID: Map<string, EmoteDef> = new Map(EMOTES.map((e) => [e.id, e]));

/** Number of slots around the wheel. */
export const WHEEL_SLOTS = 8;

/** Default slot assignment (ids), used until the player customizes it. */
export const DEFAULT_WHEEL: string[] = ['clap', 'nice', 'cheer', 'thumbsup', 'fire', 'laugh', 'boo', 'cool'];

const STORE_KEY = 'carom.emotes';

/** Load the saved wheel layout (8 emote ids), falling back to the default. */
export function loadWheel(): string[] {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr) && arr.length === WHEEL_SLOTS && arr.every((id) => EMOTES_BY_ID.has(id))) {
        return arr as string[];
      }
    }
  } catch {
    /* ignore malformed storage */
  }
  return [...DEFAULT_WHEEL];
}

/** Persist a wheel layout. */
export function saveWheel(ids: string[]): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(ids));
  } catch {
    /* ignore */
  }
}
